import { Nullable } from 'types/util/Nullable';
import { v4 } from 'uuid';

/**
 * Encodes a UUID to a compact 22-char base64 string safe for use in search params.
 * Reduces each UUID from 36 chars to 22 chars (~39% smaller).
 *
 * Example: "a80dff41-de6a-4256-9ac6-93424e3c9006" → "qA3_Qd5qQlaaypNEnjyQBg"
 */
export function encodeUuidForSearchParam(id: string): string {
  const hex = id.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  const base64 = btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(''));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decodes a compact 22-char search param value back to a UUID.
 * Also accepts a plain UUID string for backwards compatibility with existing URLs.
 *
 * Example: "qA3_Qd5qQlaaypNEnjyQBg" → "a80dff41-de6a-4256-9ac6-93424e3c9006"
 */
export function decodeUuidFromSearchParam(encoded: string): string {
  if (validateUuid(encoded)) {
    return encoded;
  }

  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const hex = Array.from(binary, (c) => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    const decoded = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    return validateUuid(decoded) ? decoded : encoded;
  } catch {
    return encoded;
  }
}

/**
 * Encodes an array of UUIDs to a compact comma-separated search param string.
 */
export function encodeUuidsForSearchParam(ids: string[]): string {
  return ids.map(encodeUuidForSearchParam).join(',');
}

/**
 * Decodes a compact comma-separated search param string back to an array of UUIDs.
 */
export function decodeUuidsFromSearchParam(encoded: string | null | undefined): string[] {
  if (!encoded) {
    return [];
  }
  return encoded.split(',').filter(Boolean).map(decodeUuidFromSearchParam);
}

export function uuid() {
  return v4();
}

export function validateUuid(test: string) {
  const regexMatchUuidV4 = new RegExp(/^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i);
  return regexMatchUuidV4.test(test);
}

export function uuidIfValid(uuid: string): Nullable<string> {
  return validateUuid(uuid) ? uuid : null;
}
